import { useCallback, useEffect, useMemo, useState } from "react";
import { SPINESIM_MODALITIES } from "../classes/enums/MedicalImagesEnum";
import { CHECK_CONSTANTS } from "../classes/constants/checkConstants";
import { useRadiodensityMeasurements } from "./queries/useRadiodensityMeasurements";
import useRadiodensityMeasurementsSync from "./useRadiodensityMeasurementsSync";

/**
 * Custom hook to manage radio density functionality in Check module
 * @param {Object} params - Hook parameters
 * @returns {Object} Radio density state and functions
 */
const useRadioDensityManager = ({
  areViewsReady,
  renderClient,
  instances,
  dataBundle,
  remoteKey,
  areDrrInstancesValidated,
  navigateToValidation,
  routeState,
  groupedThumbnailsStandard,
  handleSelectModality,
  digitalTwinId,
}) => {
  const [isRadioDensityActive, setIsRadioDensityActive] = useState(false);
  const [radioDensityStatistics, setRadioDensityStatistics] = useState(null);
  const [radioDensityInitialized, setRadioDensityInitialized] = useState(false);

  // Fetch persisted measurements from database by digital twin ID
  const { measurementsByVertebra, loading: measurementsLoading, refetch: refetchMeasurements } =
    useRadiodensityMeasurements(digitalTwinId);

  // Set up sync hook - pass refetch for immediate refresh after POST
  const { isSyncing, syncAllStatistics } = useRadiodensityMeasurementsSync({
    digitalTwinId,
    measurementsByVertebra,
    refetchMeasurements,
  });

  // Callback: Update radio density statistics and trigger sync
  // Now receives per-vertebra dimensions: vertebraDimensions = { entityId: { height, diameter }, ... }
  const handleRadioDensityStatisticsUpdate = useCallback(
    (stats, vertebraDimensions) => {
      setRadioDensityStatistics(prev => {
        // Merge new statistics with existing ones to keep all vertebrae visible
        return { ...prev, ...stats };
      });

      // Trigger sync to database with debouncing
      // Pass the per-vertebra dimensions for proper sync
      if (digitalTwinId && stats && vertebraDimensions) {
        syncAllStatistics(stats, vertebraDimensions);
      }
    },
    [digitalTwinId, syncAllStatistics]
  );

  // Callback: Toggle radio density mode
  const handleToggleRadioDensity = useCallback(() => {
    // Check if DRR is validated before allowing radiodensity
    if (!areDrrInstancesValidated) {
      navigateToValidation(
        routeState.params.patientId,
        routeState.params.twinId,
        SPINESIM_MODALITIES.DRR
      );
      return;
    }

    setIsRadioDensityActive(prev => {
      const newState = !prev;

      if (newState) {
        // Ensure we switch back to the CT modality when activating radiodensity
        const hasCt = (groupedThumbnailsStandard || []).some(
          group => group.modality === SPINESIM_MODALITIES.CT
        );
        if (hasCt) {
          handleSelectModality(null, SPINESIM_MODALITIES.CT);
        }
      } else {
        // Going back to CT view
        const hasCt = (groupedThumbnailsStandard || []).some(
          group => group.modality === SPINESIM_MODALITIES.CT
        );
        if (hasCt) {
          handleSelectModality(null, SPINESIM_MODALITIES.CT);
        }
      }

      return newState;
    });
  }, [
    handleSelectModality,
    groupedThumbnailsStandard,
    areDrrInstancesValidated,
    navigateToValidation,
    routeState.params.patientId,
    routeState.params.twinId,
  ]);

  // Build per-vertebra dimensions map from persisted measurements
  // Keys are entity IDs (numbers), values are { height, diameter }
  const initialVertebraDimensions = useMemo(() => {
    const dimensionsMap = {};

    // Populate from persisted measurements (keyed by entity ID)
    Object.entries(measurementsByVertebra).forEach(([vertebraId, measurement]) => {
      dimensionsMap[vertebraId] = {
        height: measurement.height ?? CHECK_CONSTANTS.RADIO_DENSITY_DEFAULTS.HEIGHT,
        diameter: measurement.diameter ?? CHECK_CONSTANTS.RADIO_DENSITY_DEFAULTS.DIAMETER,
      };
    });

    return dimensionsMap;
  }, [measurementsByVertebra]);

  // Effect: Initialize radio density (cylinders first, then fetch statistics)
  // Uses per-vertebra persisted dimensions if available
  useEffect(() => {
    if (
      !areViewsReady ||
      !renderClient ||
      !instances?.ct_instance_id ||
      radioDensityInitialized ||
      measurementsLoading
    ) {
      return;
    }

    const initializeRadioDensity = async () => {
      try {
        // Get all CT vertebrae from annotations
        const ctInstanceId =
          instances.ct_instance_id._id || instances.ct_instance_id;
        const ctAnnotations = dataBundle.annotations?.[ctInstanceId];

        if (!ctAnnotations || !Array.isArray(ctAnnotations)) {
          return;
        }

        const vertebraeEntityIds = ctAnnotations
          .map(annotation => annotation?.unique_identifier)
          .filter(id => id != null);

        if (vertebraeEntityIds.length === 0) {
          return;
        }

        // Group vertebrae by their dimensions for efficient initialization
        // Backend preserves existing dimensions, so we call once per unique dimension set
        const dimensionGroups = {};

        vertebraeEntityIds.forEach(entityId => {
          const dims = initialVertebraDimensions[entityId] || {
            height: CHECK_CONSTANTS.RADIO_DENSITY_DEFAULTS.HEIGHT,
            diameter: CHECK_CONSTANTS.RADIO_DENSITY_DEFAULTS.DIAMETER,
          };
          const key = `${dims.height}_${dims.diameter}`;
          if (!dimensionGroups[key]) {
            dimensionGroups[key] = {
              entityIds: [],
              height: dims.height,
              diameter: dims.diameter,
            };
          }
          dimensionGroups[key].entityIds.push(entityId);
        });

        // Initialize cylinders - first call with all vertebrae using defaults,
        // then update each group with their specific dimensions
        const groups = Object.values(dimensionGroups);

        // If all vertebrae have the same dimensions, single call is enough
        if (groups.length === 1) {
          await renderClient.updateRadiodensityCylinders(
            remoteKey,
            groups[0].entityIds,
            groups[0].diameter,
            groups[0].height
          );
        } else {
          // Multiple dimension groups - initialize all with defaults first
          await renderClient.updateRadiodensityCylinders(
            remoteKey,
            vertebraeEntityIds,
            CHECK_CONSTANTS.RADIO_DENSITY_DEFAULTS.DIAMETER,
            CHECK_CONSTANTS.RADIO_DENSITY_DEFAULTS.HEIGHT
          );

          // Then update each group with their specific dimensions
          for (const group of groups) {
            await renderClient.updateRadiodensityCylinders(
              remoteKey,
              group.entityIds,
              group.diameter,
              group.height
            );
          }
        }

        // Fetch statistics (now that cylinders exist with correct dimensions)
        const stats = await renderClient.getRadioDensityStatistics(
          remoteKey,
          vertebraeEntityIds
        );

        setRadioDensityStatistics(stats);

        // Sync initial measurements to database for vertebrae without persisted data
        // Build dimensions map for all vertebrae (use persisted or defaults)
        const allDimensions = {};
        vertebraeEntityIds.forEach(entityId => {
          allDimensions[entityId] = initialVertebraDimensions[entityId] || {
            height: CHECK_CONSTANTS.RADIO_DENSITY_DEFAULTS.HEIGHT,
            diameter: CHECK_CONSTANTS.RADIO_DENSITY_DEFAULTS.DIAMETER,
          };
        });

        // This will POST for vertebrae without existing measurements, PATCH for those with
        if (stats && digitalTwinId) {
          syncAllStatistics(stats, allDimensions);
        }

        setRadioDensityInitialized(true);
      } catch (error) {
        console.error("Error initializing radiodensity:", error);
      }
    };

    initializeRadioDensity();
  }, [
    areViewsReady,
    renderClient,
    instances,
    dataBundle.annotations,
    radioDensityInitialized,
    remoteKey,
    measurementsLoading,
    initialVertebraDimensions,
    digitalTwinId,
    syncAllStatistics,
  ]);

  // Effect: Auto-deactivate radio density when switching away from CT modality
  const autoDeactivateOnModalityChange = useCallback(selectedModality => {
    if (selectedModality !== SPINESIM_MODALITIES.CT) {
      setIsRadioDensityActive(false);
    }
  }, []);

  return {
    isRadioDensityActive,
    radioDensityStatistics,
    radioDensityInitialized,
    handleRadioDensityStatisticsUpdate,
    handleToggleRadioDensity,
    autoDeactivateOnModalityChange,
    initialVertebraDimensions,
    measurementsByVertebra,
    isSyncing,
  };
};

export default useRadioDensityManager;
